import { t } from "@lingui/core/macro";
import { createFileRoute, Navigate } from "@tanstack/react-router";
import { useRef, useState } from "react";
import { useSelector } from "react-redux";

import { selectMaybeActiveAccountEid } from "@/redux/app/appSelectors";
import { useCreateSellOrderMutation } from "@/routes/_authenticated/trade/-api/trade";
import type { WithdrawalVerification } from "@/shared/api/types/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/shared/components/ui/card";
import { estimateFee } from "@/util/feeSchedule";
import { formatCurrency } from "@/util/numberUtils";
import {
  type Metal,
  type SaleLine,
  formatSaleOunces,
  metalLabel,
  salesFromQuantities,
  saleTotal,
} from "@/util/withdrawal";
import { cashSaleEligibility } from "@/util/withdrawalRules";

import { formatDepositAccount } from "../../-components/requestView";
import {
  cashEstimateNote,
  HelpBlock,
  SaleLineRows,
  Screen,
  SuccessDialog,
  SummaryRow,
  useFlow,
  useFlowNavigate,
} from "../../-components/Withdrawal/WithdrawalFlow";
import {
  FinalCheck,
  NextSteps,
  SubmitErrorBanner,
  submitErrorMessage,
} from "../../-components/Withdrawal/WithdrawalReview";

const saleSummaryText = (sales: SaleLine[]): string =>
  sales.map((line) => `${formatSaleOunces(line.ounces)} oz ${metalLabel(line.metal)}`).join(" + ");

const useResolvedBank = () => {
  const { inputs, cash } = useFlow();
  const match = inputs.paymentMethods.find(
    (method) => method.id === cash.bankId && method.verified && method.verificationType === "plaid",
  );
  return match ? { bankName: match.nickname, mask: match.mask, accountHolderName: match.accountHolderName } : null;
};

const metalRequestFor = (
  metal: Metal,
  sales: readonly SaleLine[],
  holdings: readonly { metal: Metal; availableOz: number }[],
) => {
  const ounces = sales.find((line) => line.metal === metal)?.ounces ?? 0;
  const holding = holdings.find((candidate) => candidate.metal === metal);
  return { ounces, allAvailable: ounces > 0 && holding !== undefined && ounces === holding.availableOz };
};

const CashReviewScreen = () => {
  const navigate = useFlowNavigate();
  const { inputs, cash, reset } = useFlow();
  const eid = useSelector(selectMaybeActiveAccountEid);
  const sales = salesFromQuantities(inputs.holdings, cash.quantities);
  const proceeds = saleTotal(sales);
  const bank = useResolvedBank();
  const [createSellOrder, { isLoading: submitting }] = useCreateSellOrderMutation();
  const [submittedTaskId, setSubmittedTaskId] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const submissionId = useRef(crypto.randomUUID()).current;

  const eligible = cashSaleEligibility({
    holdings: inputs.holdings,
    quantities: cash.quantities,
    ...inputs.limits,
  }).allowed;
  if (!eligible) return <Navigate to="/trade/withdraw/cash" replace />;
  if (!bank) return <Navigate to="/trade/withdraw/cash/payout" replace />;

  const submit = async (verification: WithdrawalVerification) => {
    if (!eid || !cash.bankId) return;
    setSubmitError(null);
    try {
      const task = await createSellOrder({
        eid,
        paymentMethodId: cash.bankId,
        payoutMethod: cash.payoutMethod,
        goldOunces: metalRequestFor("gold", sales, inputs.holdings).ounces,
        silverOunces: metalRequestFor("silver", sales, inputs.holdings).ounces,
        submissionId,
        ...verification,
      }).unwrap();
      setSubmittedTaskId(task.task_id ?? "");
    } catch (error) {
      setSubmitError(submitErrorMessage(error));
      throw error;
    }
  };

  const summary = [
    { label: t`Withdrawal`, value: saleSummaryText(sales) },
    {
      label: t`Estimated fee`,
      value: formatCurrency(estimateFee(proceeds) === 0 ? 0 : -estimateFee(proceeds)),
    },
    { label: t`Estimated proceeds`, value: formatCurrency(proceeds - estimateFee(proceeds)) },
    { label: t`Deposit account`, value: formatDepositAccount(bank.bankName, bank.mask) },
    { label: t`Payout method`, value: cash.payoutMethod === "ach" ? t`ACH` : t`Wire` },
  ];

  return (
    <Screen subtitle={t`Review your withdrawal request.`} step={{ index: 4, total: 4 }}>
      <div className="grid gap-4 @md:grid-cols-[1.4fr_1fr] @md:items-start">
        <Card className="shadow-none">
          <CardHeader>
            <CardTitle className="text-base">{t`Withdrawal summary`}</CardTitle>
          </CardHeader>
          <CardContent>
            <SummaryRow label={t`Transaction Type`} value={t`Sell Metal for Cash`} />
            <SaleLineRows sales={sales} totalLabel={t`Estimated Proceeds`} />
            <SummaryRow label={t`Recipient Bank`} value={bank ? bank.bankName : "—"} />
            <SummaryRow label={t`Account`} value={bank ? `••••${bank.mask}` : "—"} />
            <p className="mt-3 text-xs text-muted-foreground">{cashEstimateNote()}</p>
          </CardContent>
        </Card>
        <NextSteps
          items={[
            t`Your metal will be sold at the next trading window.`,
            t`You will receive a trade confirmation by email.`,
            t`You can track the status of your withdrawal under Recent Transactions.`,
          ]}
        />
      </div>

      <FinalCheck
        lines={summary}
        onSubmit={submit}
        onBack={() => navigate("cash/payout")}
        note={cashEstimateNote()}
        submitting={submitting}
      >
        {submitError && <SubmitErrorBanner message={submitError} />}
      </FinalCheck>
      <HelpBlock />
      <SuccessDialog
        open={submittedTaskId !== null}
        statusTaskId={submittedTaskId ?? ""}
        headline={t`Your withdrawal is in process`}
        note={t`You can track this request in Recent Transactions.`}
        lines={summary}
        onDone={() => {
          reset();
          navigate("");
        }}
      />
    </Screen>
  );
};

export const Route = createFileRoute("/_authenticated/trade/withdraw/cash/review")({
  component: CashReviewScreen,
});
